import { LinkType, AddressInfo } from 'src/services/common/common.types';
import type { Category, EntityLabel } from 'src/services/utils/utils.types';

import type { SettingsFormData } from 'src/sections/connections/modal/create-connection-modal/components/connection-settings-form';

import { TenantType } from 'src/types/enums';

import type { Organization } from '../auth/auth.types';
import type { Contact } from '../contacts/contacts.types';
import type { AccountStatus } from '../account/account.types';
import type { AttachedThread } from '../threads/threads.types';
import type { PageOptions, PaginatedResponse, SortConfig } from '../api.types';
import type { IJob, FeeType, JobFilters } from '../jobs/jobs.types';
import type { DocumentFile, DocumentType } from '../files/file.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';
import type { User, CompanySizeRange } from '../organisation/organisation.types';
import { EntityLinkType } from '../common.types';


export type GetConnectionsRequest = {
  organization_id: string;
  search_query?: string;
  status?: ConnectionStatus;
} & PageOptions;

export interface GetConnectionDetailsProps {
  organization_id: string;
  request_id: string;
}

export interface Connection {
  id: string;
  status: ConnectionStatus;
  connected_organization: ConnectionOrg;
  thread: { id: string };
  approved_specializations: Category[];
  job_limit: null | number;
  terms_type: ConnectionTermsType;
  default_fee_type: FeeType;
  default_fee_value: number;
  created_at: string;
}

export interface ConnectionOrg {
  id: string;
  type: TenantType;
  name: string;
  company_information: string;
}

export enum RequestStatus {
  UNAPPROVED = 'unapproved',
  DENIED = 'denied',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
  CLOSED = 'closed',
}

export enum ConnectionStatus {
  PENDING = 'pending',
  UNAPPROVED = 'unapproved',
  CONNECTED = 'connected',
  CLOSED = 'closed',
}

export interface ConnectionRequestActionPayload {
  connectionId: string;
  action: ConnectionRequestAction;
}

export interface ConnectionRequestAction {
  action: ConnectionActionOption;
  connection_settings?: ConnectionSettings;
}

export enum ConnectionActionOption {
  // approve, deny from sender, accept, reject from receiver
  Accept = 'accept',
  Reject = 'reject',
  Approve = 'approve',
  Deny = 'deny',
}

export enum RequestInviteCategory {
  PRIMARY = 'primary',
  ADDITIONAL = 'additional'
}

export enum ContactLinkType {
  PRIMARY = 'primary',
  OTHER = 'other',
}

export interface ConnectionSettings {
  job_limit_applied: boolean;
  jobs_limit: number | null;
  specializations: string[];
  default_fee_type: string;
  default_fee_value: number;
}

export type TemplateData = {
  date?: string;
  rate?: string;
  payment_terms?: string;
  special_conditions?: string;
};

export interface ConnectionRequest {
  id: string;
  status: RequestStatus;
  client: ICompanyProfile;
  recruiter: ICompanyProfile;

  initiator_type: TenantType;
  initiator_account: {
    id: string;
    first_name: string;
    last_name: string;
  };

  created_at: string;
  settings_data: SettingsFormData;
  template_data: TemplateData;

  invites: { email: string; name: string, category: RequestInviteCategory }[];

  terms_type: ConnectionTermsType;
  documents: { id: string; name: string; file: { public_path: string } }[];
  requested_terms: { id: string, content: string }[]
}

export interface CompanyProfileLink {
  type: LinkType;
  url: string;
}

export interface ICompanyProfile {
  id: string;
  status?: AccountStatus;
  company_name: string;
  company_type: TenantType;
  company_information: string;
  organization?: Pick<Organization, 'id' | 'status' | 'type'>;
  type: CompanyProfileType;
  company_size?: CompanySizeRange;
  company_phone?: string;
  company_logo?: {
    id: string;
    public_path: string;
  } | null;
  company_website?: string;
  country_code?: string;
  tax_id?: string;
  availability_shared?: boolean;
  links?: CompanyProfileLink[];
  labels?: EntityLabel[];
  primary_address?: AddressInfo;
  thread?: AttachedThread;
  account_owner?: Partial<User>;
}

export interface ICompanyDocument {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export interface IJobDocument {
  id: string;
  document: DocumentFile;
  created_at: Date;
}

export interface CreateCompanyDocumentDto {
  file_id: string;
  name: string;
  type: DocumentType;
}

export interface IContactLink {
  id: string;
  type?: EntityLinkType;
  contact: Contact;
}

export interface IContactCompanyLink {
  id: string;
  company_profile: ICompanyProfile;
}

export interface ICompanyContactLink {
  id: string;
  type: EntityLinkType;
  contact: Contact;
}

export interface CreateLocalCompanyDto {
  company_name: string;
  company_information: string;
  company_size?: CompanySizeRange;
  country_code?: string;
  tax_id?: string;
  company_website?: string;
  labels?: EntityLabel[];
  custom_fields?: UpdateCustomFieldValueDto;
}

export interface BulkCompanyImportDto {
  organization_id?: string;
  companies: {
    company_name: string;
    company_information?: string;
    company_type?: TenantType;
    tax_id?: string;
    company_website?: string;
    linkedin_link?: string;
    x_link?: string;
    facebook_link?: string;
    github_link?: string;
    street?: string;
    city?: string;
    state?: string;
    country_code?: string;
  }[];
}

export interface UpdateLocalCompanyDto {
  company_name?: string;
  company_information?: string;
  company_size?: CompanySizeRange;
  country_code?: string;
  company_phone?: string;
  tax_id?: string;
  company_website?: string;
  links?: CompanyProfileLink[];
  labels?: EntityLabel[];
  company_logo_id?: string;
  primary_address?: AddressInfo;
  custom_fields?: UpdateCustomFieldValueDto;
}

export interface InitiateConnectionRequestPops {
  organization_id: string;
  request: ConnectionRequestData;
}

export interface UpdateConnectionSettingsPops {
  organization_id: string;
  connection_id: string;
  settings: ConnectionSettings;
}

export interface ConnectionRequestData {
  target: ConnectionTargetData;

  terms_settings: ConnectionTermsSettings;

  // only applicable if tenant type is client
  connection_settings?: ConnectionSettings;
}

export interface ConnectionTargetData {
  target_id?: string;
  company_name?: string;
  email?: string;
  name?: string;
  additional_invites?: { email: string; }[];
}

export interface ConnectionTermsSettings {
  // Option 1
  terms_type?: ConnectionTermsType;
  terms_id?: string;

  // Option 2
  documents?: [
    {
      id: string;
    },
  ];

  // Option 3
  // TOB //template
  template_data?: {
    date: string;
    rate: string;
    payment_terms: string;
    special_conditions?: string;
  };
}

export enum TermsType {
  TERMS_OF_USE = 'terms_of_use',
  TERMS_OF_ENGAGEMENT = 'terms_of_engagement',
}

export interface UnlinkConnectionProps {
  organization_id: string;
  connection_id: string;
}

export type GetConnectionRequestProps = {
  organization_id: string;
  target: ConnectionTarget;
} & PageOptions;

export enum ConnectionTarget {
  Incoming = 'incoming',
  Outgoing = 'outgoing',
}

export enum ConnectionTermsType {
  Terms_Of_Engagement = 'terms_of_engagement',
  Template = 'tob_template',
  Custom = 'tob_upload',
}

export type ConnectionSearchProps = {
  params: { include_system_profiles?: boolean, search_query?: string } & PageOptions & JobFilters;
  target: InteractedTargets;
};

export type CompanySearchProps = {
  params: { include_system_profiles?: boolean, search_query?: string, company_type?: TenantType } & PageOptions & JobFilters & Partial<SortConfig>;
};

export type CompanyLeanListProps = {
  params: { search_query?: string, company_type?: TenantType, column_filters?: Record<string, string> } & PageOptions & Partial<SortConfig>;
};

// Flat row from GET /my/companies/list (the lean list read path, JT1-1527/1528, item 27).
export interface ICompanyListItem {
  id: string;
  company_name: string;
  type: string;
  company_type: TenantType | null;
  city: string | null;
  logo_url: string | null;
  account_owner_name: string | null;
  job_count: number;
  contact_count: number;
  last_activity_at: string | null;
}

export enum InteractedTargets {
  clients = 'clients',
  recruiters = 'recruiters',
}

export enum CompanyProfileType {
  UNLINKED = 'unlinked',
  LINKED = 'linked',
  PRIMARY = 'primary',
  SYSTEM = 'system',
}

export type GetConnectionResourceProps = {
  params: PageOptions;
  profile_id: string;
  target: InteractedTargets;
};

export type GetProfileById = {
  profile_id: string;
  target: InteractedTargets;
};

export type ProfileJobsResponse = PaginatedResponse<ProfileJob>;

export type ProfileJob = Pick<
  IJob,
  'description' | 'id' | 'industry' | 'status' | 'title' | 'verification_status' | 'vacancies'
>;
